<template>
    <Modal
        :modelValue="modelValue"
        :title="shareTitle || $t('ChatDetail.share_message_title', 'Share message')"
        :header="true"
        :footer="false"
        :closeOnBackdrop="!isSubmitting"
        :closeIcon="!isSubmitting"
        className="share-message-modal"
        :styles="{ width: '560px', maxWidth: 'calc(100vw - 32px)' }"
        bodyClasses="share-message-modal__body"
        @close="handleClose"
    >
        <template #body>
            <div class="share-message-modal__content">
                <div class="share-message-modal__search">
                    <input
                        v-model.trim="search"
                        type="text"
                        class="form-control"
                        :placeholder="searchPlaceholder || $t('ChatDetail.share_message_search_placeholder', 'Search private chats or group chats')"
                        :disabled="isSubmitting"
                    />
                </div>

                <div class="share-message-modal__preview">
                    <div class="font-weight-500 mb-5px">{{ previewLabel || $t('ChatDetail.share_message_preview_label', 'Message preview') }}</div>
                    <div class="share-message-modal__preview-text text-ellipsis">
                        {{ previewText }}
                    </div>
                </div>

                <div class="share-message-modal__list">
                    <div
                        v-for="item in filteredConversations"
                        :key="item.id || 'user:' + item.userId"
                        class="share-message-modal__item"
                        :class="{ active: selectedIds.includes(item.id || 'user:' + item.userId) }"
                        @click="toggleSelection(item.id || 'user:' + item.userId)"
                    >
                        <div class="d-flex align-items-center">
                            <GroupAvatarStack
                                v-if="item.isGroupChat"
                                :groupImage="item.GroupChatImage"
                                :groupName="item.name"
                                :members="item.groupMembers"
                                :size="40"
                            />
                            <UserProfile
                                v-else
                                :data="{ title: item.name, image: item.image }"
                                width="40px"
                                :thumbnail="''"
                                :showDot="false"
                            />
                            <div class="ml-10px share-message-modal__meta">
                                <div class="font-weight-500">{{ item.name }}</div>
                                <div class="font-size-12 gray">
                                    {{ item.isGroupChat ? $t('ChatDetail.share_message_group_chat', 'Group chat') : item.isNewUser ? $t('Filters.user', 'User') : $t('ChatDetail.share_message_private_chat', 'Private chat') }}
                                </div>
                            </div>
                        </div>
                        <input
                            :type="singleSelection ? 'radio' : 'checkbox'"
                            :name="singleSelection ? 'share-message-recipient' : undefined"
                            :checked="selectedIds.includes(item.id || 'user:' + item.userId)"
                            @click.stop="toggleSelection(item.id || 'user:' + item.userId)"
                            :disabled="isSubmitting"
                        />
                    </div>

                    <div v-if="!filteredConversations.length" class="share-message-modal__empty">
                        {{ $t('ChatDetail.share_message_empty', 'No conversations found') }}
                    </div>
                </div>

                <div class="share-message-modal__caption">
                    <label class="font-weight-500 mb-5px d-block">{{ captionLabel || $t('ChatDetail.share_message_caption_label', 'Caption') }}</label>
                    <textarea
                        v-model="caption"
                        class="form-control"
                        rows="3"
                        :placeholder="captionPlaceholder || $t('ChatDetail.share_message_caption_placeholder', 'Add a message (optional)')"
                        :disabled="isSubmitting"
                    />
                </div>

                <div class="share-message-modal__footer">
                    <button class="outline-secondary" :disabled="isSubmitting" @click="handleClose">{{ $t('Common.cancel', 'Cancel') }}</button>
                    <button
                        class="btn-primary ml-10px"
                        :class="{ 'share-message-modal__submit--disabled': !selectedIds.length || isSubmitting }"
                        :disabled="!selectedIds.length || isSubmitting"
                        @click="submitShare"
                    >
                        {{ isSubmitting ? $t('ChatDetail.share_message_submitting', 'Sharing...') : $t('ChatDetail.share', 'Share') }}
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { computed, inject, ref, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { useI18n } from "vue-i18n";
import Modal from "@/components/atom/Modal/Modal.vue";
import UserProfile from "@/components/atom/UserProfile/UserProfile.vue";
import GroupAvatarStack from "@/components/atom/GroupAvatarStack/GroupAvatarStack.vue";
import { useGetterFunctions } from "@/composable";
import { apiRequest } from "@/services";
import * as env from "@/config/env";

const userId = inject("$userId");
const companyId = inject("$companyId");
const { getters, dispatch } = useStore();
const { getUser } = useGetterFunctions();
const $toast = useToast();
const { t } = useI18n();

const emit = defineEmits(["close", "shared"]);

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    message: {
        type: Object,
        default: () => ({}),
    },
    currentConversationId: {
        type: String,
        default: "",
    },
    singleSelection: {
        type: Boolean,
        default: false,
    },
    peopleOnly: {
        type: Boolean,
        default: false,
    },
    shareTitle: {
        type: String,
        default: "",
    },
    searchPlaceholder: {
        type: String,
        default: "",
    },
    previewLabel: {
        type: String,
        default: "",
    },
    captionLabel: {
        type: String,
        default: "",
    },
    captionPlaceholder: {
        type: String,
        default: "",
    },
});

const search = ref("");
const caption = ref("");
const selectedIds = ref([]);
const isSubmitting = ref(false);

const currentUserId = computed(() => userId?.value || userId || "");
const conversations = computed(() => getters["mainChat/conversationsList"] || []);
const allUsers = computed(() => getters["users/users"] || []);

const normalizedConversations = computed(() => {
    return (conversations.value || [])
        .filter((item) => Number(item.deletedStatusKey || 0) === 0)
        .map((item) => {
            const id = String(item.id || item._id || "");
            const isGroupChat = item.chatType === "groupChat";
            const memberIds = Array.isArray(item.AssigneeUserId) ? item.AssigneeUserId.map((value) => String(value)) : [];
            const otherId = !isGroupChat
                ? memberIds.find((value) => String(value) !== String(currentUserId.value))
                : "";
            const otherUser = otherId ? getUser(otherId) : null;
            const name = isGroupChat
                ? (item.name || item.GroupName || item.TaskName || t("ChatDetail.share_message_group_chat", "Group chat"))
                : (otherUser?.Employee_Name || item.name || item.TaskName || t("ChatDetail.share_message_private_chat", "Private chat"));

            return {
                ...item,
                id,
                name,
                memberIds,
                isGroupChat,
                image: otherUser?.Employee_profileImageURL || "",
                groupMembers: memberIds
                    .map((value) => {
                        const user = getUser(value);
                        return {
                            id: value,
                            name: user?.Employee_Name || t("Filters.user", "User"),
                            image: user?.Employee_profileImageURL || "",
                            ghostUser: user?.ghostUser,
                        };
                    })
                    .filter((member) => member.id && !member.ghostUser),
            };
        })
        .filter((item) => item.id && String(item.id) !== String(props.currentConversationId));
});

const normalizedUsers = computed(() => {
    const otherUsers = allUsers.value.filter((u) => String(u._id) !== String(currentUserId.value));

    return otherUsers.map((u) => ({
        id: "",
        userId: String(u._id),
        name: u.Employee_Name || u.name || t("Filters.user", "User"),
        image: u.Employee_profileImageURL || "",
        isGroupChat: false,
        isNewUser: true,
        memberIds: [String(u._id)],
    }));
});

const filteredConversations = computed(() => {
    const keyword = search.value.toLowerCase();

    // Matched users (no existing conversation)
    const matchedUsers = normalizedUsers.value.filter(
        (u) => !normalizedConversations.value.some((c) => !c.isGroupChat && c.memberIds.includes(u.userId))
    );

    // Combine both
    const availableConversations = props.peopleOnly
        ? normalizedConversations.value.filter((item) => !item.isGroupChat)
        : normalizedConversations.value;
    let combined = [...matchedUsers, ...availableConversations];

    if (!keyword) {
        return combined;
    }

    return combined.filter((item) => item.name.toLowerCase().includes(keyword));
});

const previewText = computed(() => {
    const message = props.message || {};

    if (message.type === "sticker") {
        return t("Common.sticker", "Sticker");
    }

    if (message.type === "image") {
        return message.mediaOriginalName || message.mediaName || t("Common.image", "Image");
    }

    if (message.type === "audio") {
        return message.mediaOriginalName || message.mediaName || t("Common.audio_message", "Audio");
    }

    if (message.type === "video") {
        return message.mediaOriginalName || message.mediaName || t("Common.video", "Video");
    }

    if (message.type && !["text", "link"].includes(message.type)) {
        return message.mediaOriginalName || message.mediaName || t("Common.attachment", "Attachment");
    }

    return message.message || "";
});

watch(
    () => props.modelValue,
    (value) => {
        if (value) {
            search.value = "";
            caption.value = "";
            selectedIds.value = [];
            isSubmitting.value = false;
        }
    }
);

function toggleSelection(id) {
    if (isSubmitting.value) return;

    if (selectedIds.value.includes(id)) {
        selectedIds.value = selectedIds.value.filter((item) => item !== id);
        return;
    }

    if (props.singleSelection) {
        selectedIds.value = [id];
        return;
    }

    selectedIds.value = [...selectedIds.value, id];
}

function buildSharedMessagePayload(message = {}) {
    return {
        message: message.message || "",
        type: message.type || "text",
        mediaURL: message.mediaURL || "",
        mediaName: message.mediaName || "",
        mediaOriginalName: message.mediaOriginalName || "",
        mediaSize: Number(message.mediaSize || 0),
    };
}

function handleClose() {
    if (isSubmitting.value) return;
    emit("close", false);
}

async function submitShare() {
    if (!selectedIds.value.length || isSubmitting.value) return;

    const targets = selectedIds.value.map((id) => {
        if (id.startsWith("user:")) {
            return {
                userId: id.replace("user:", ""),
                memberIds: [],
            };
        }

        const conv = normalizedConversations.value.find((c) => c.id === id);
        return {
            conversationId: id,
            memberIds: conv?.memberIds || [],
        };
    });

    if (!targets.length) return;

    isSubmitting.value = true;

    try {
        const payload = {
            data: {
                userId: currentUserId.value,
                targets,
                messages: [buildSharedMessagePayload(props.message)],
                caption: caption.value.trim(),
            },
        };

        await apiRequest("post", `${env.CHATS}/share`, payload);
        emit("shared", {
            targetCount: targets.length,
        });
        emit("close", false);
    } catch (error) {
        console.error("shareMessage submitShare error:", error);
        $toast.error(t("ChatDetail.share_message_failed", "Could not share message."), { position: "top-right" });
    } finally {
        isSubmitting.value = false;
    }
}

onMounted(() => {
    if (!allUsers.value.length && companyId.value) {
        dispatch("users/setUsers", { cid: companyId.value });
    }
});
</script>

<style scoped>
.share-message-modal__content {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.share-message-modal__preview {
    padding: 12px;
    border: 1px solid #e5e5ea;
    border-radius: 8px;
    background: #fafafa;
}

.share-message-modal__preview-text {
    max-width: 100%;
}

.share-message-modal__list {
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #e5e5ea;
    border-radius: 8px;
    background: #fff;
}

.share-message-modal__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    border-bottom: 1px solid #f2f2f7;
    cursor: pointer;
}

.share-message-modal__item:last-child {
    border-bottom: none;
}

.share-message-modal__item.active {
    background: #f3f8ff;
}

.share-message-modal__meta {
    min-width: 0;
}

.share-message-modal__meta > div:first-child {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 320px;
}

.share-message-modal__empty {
    padding: 24px 12px;
    text-align: center;
    color: #818181;
}

.share-message-modal__footer {
    display: flex;
    justify-content: flex-end;
}

.share-message-modal__caption textarea {
    min-height: 96px;
    resize: vertical;
}

.share-message-modal__submit--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}
</style>
